// `/dancers` — the Manager-facing roster layout. A thin primary adapter
// (ADR 0005): path, flag gate, cache warm, and the page from `roster/ui/`.

import {
	createFileRoute,
	Outlet,
	useNavigate,
	useRouteContext,
	useRouterState,
} from '@tanstack/react-router';

import { featureFlagsQueryKey } from '~/_shared/feature-flags/evaluate-flags';
import { evaluateFeatureFlagsServerFn } from '~/_shared/feature-flags/evaluate-flags.fn';
import { gateDancersRoute } from '~/bounded-contexts/roster/application/dancers-route-guard';
import { listTalentServerFn } from '~/bounded-contexts/roster/application/list-talent.fn';
import { DancersNavGuard } from '~/bounded-contexts/roster/ui/dancers-page/dancers-nav-guard';
import { DancersPage } from '~/bounded-contexts/roster/ui/dancers-page/dancers-page';
import {
	isDetailPaneOpen,
	selectedTalentId,
} from '~/bounded-contexts/roster/ui/dancers-page/detail-pane-open';
import { rosterQueryKey } from '~/bounded-contexts/roster/ui/dancers-page/query-key';
import { UnsavedChangesProvider } from '~/bounded-contexts/roster/ui/dancers-page/unsaved-changes';
import { PublicationPill } from '~/bounded-contexts/talent-profile/ui/publication/publication-pill';
import { usePublishedProfiles } from '~/bounded-contexts/talent-profile/ui/publication/use-published-profiles';

function DancersRouteComponent() {
	const ctx = useRouteContext({ from: '/_authenticated' });
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const club_id = ctx?.scope?.club?.id ?? '';
	const published = usePublishedProfiles(club_id);
	return (
		<UnsavedChangesProvider>
			<DancersNavGuard />
			<DancersPage
				club_id={club_id}
				publication_pill={(talent_id) => (
					<PublicationPill is_published={published.has(talent_id)} />
				)}
				is_detail_open={isDetailPaneOpen(pathname)}
				selected_talent_id={selectedTalentId(pathname)}
				on_add={() => void navigate({ to: '/dancers/new' })}
				on_select={(talentId) =>
					void navigate({ to: '/dancers/$talentId', params: { talentId } })
				}
			>
				<Outlet />
			</DancersPage>
		</UnsavedChangesProvider>
	);
}

export const Route = createFileRoute('/_authenticated/dancers')({
	// The gate BLOCKS on the flags query before asserting, and
	// `revalidateIfStale` keeps the 60s staleTime meaningful — without it a
	// cached map never refetches and killing the flag would never bite.
	beforeLoad: ({ context }) =>
		gateDancersRoute(() =>
			context.queryClient.ensureQueryData({
				queryKey: featureFlagsQueryKey(context.scope.club.id),
				queryFn: () => evaluateFeatureFlagsServerFn(),
				staleTime: 60_000,
				revalidateIfStale: true,
			}),
		),
	loader: ({ context }) => {
		// Awaited on the SERVER only: whatever settles during render dehydrates, so
		// a fire-and-forget prefetch would race it into a hydration mismatch.
		const roster = context.queryClient
			.prefetchQuery({
				queryKey: rosterQueryKey(context.scope.club.id),
				queryFn: () => listTalentServerFn({ data: { archived: false } }),
			})
			.catch(() => {});
		if (typeof window === 'undefined') return roster;
	},
	component: DancersRouteComponent,
});
